import type { Distance } from "@pdfking/math"
import { Max, Min } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import { CM, MM } from "@pdfking/quantity"

import type { BoxPointList, BoxPointLists, GrowPlotter, PlotContribute } from "#internal"
import { CUT_LAYER_ID, FOLD_LAYER_ID } from "#internal"

import { MIN_SHEET_SIZE } from "./BoxConstants"
import { Corner } from "./Corner"
import { ExtractVariables } from "./ExtractVariables"
import { MirrorX } from "./MirrorX"
import { MirrorY } from "./MirrorY"
import { PlotPointsList } from "./PlotPointsList"
import { PointAccumulate } from "./PointAccumulate"

const FLAP_EXTRA = MM(1)

const PlotBoxAutoImpl =
  (flaps: boolean): GrowPlotter =>
  size => {
    const { w: h, h: w, d, s } = ExtractVariables(size, { min_s: MIN_SHEET_SIZE })

    const fw = Min(d * 1.5, h / 2 - MM(2)) as Distance
    const fd = (MM(5) + s / 2) as Distance
    const ll = MM(2)
    const fs = Min(d, CM(3)) as Distance
    const mirror_y_center = -(d + w / 2 + s) as Distance
    const mirror_x_center = ((s + h) / 2) as Distance

    // The thumb hole: a slot, round at either end
    const hole = PointAccumulate([
      { x: -2 * s, y: -(d + w / 3 + s) },
      { x: 0, y: -w / 3 },
      ...Corner(s, -s, "y"),
      ...Corner(s, s, "x"),
      { x: 0, y: w / 3 },
      ...Corner(-s, s, "y"),
      ...Corner(-s, -s, "x"),
    ] as BoxPointList)

    const initial_cut = PointAccumulate([
      { x: -s, y: -s },
      { x: -(s + fw), y: 0 },
      { x: 0, y: fd - d + 1.5 * s },
      { x: fw / 3 - fd / 2, y: -fd / 2 },
      ...Corner(fd / 2, -fd / 2, "x"),
      { x: (fw * 2) / 3, y: 0 },
      { x: s, y: -s / 2 },
      { x: -s, y: s / 2 },
      { x: -d, y: 0 },
      { x: -s * 2, y: -s },
      { x: ll - d, y: 0 },
      { x: 0, y: -w / 3 },
      { x: -(ll + s + FLAP_EXTRA), y: -s / 2 },
    ] as BoxPointList)

    const flap_points = (
      flaps
        ? [
            { x: 0, y: -(s + MM(2)) },
            { x: CM(1) - fs, y: 0 },
            // the flap's shoulder, then its side curving out to its tip
            ...Corner(-CM(1), -CM(0.3), "x"),
            { x: 0, y: fs - d },
            ...Corner(fs, s - fs + CM(0.5), "y"),
          ]
        : [
            { x: 0, y: -0.5 * d },
            { x: MM(5), y: -0.5 * d },
          ]
    ) as BoxPointList

    // One half of the cut outline; the sheet is symmetric across
    // `mirror_x_center`, so the other half is this mirrored.
    const cut_half: BoxPointLists = [
      ...PointAccumulate([{ x: 0, y: 0 }, ...Corner(-s, -s, "y")] as BoxPointList),
      ...initial_cut,
      ...MirrorY(initial_cut, mirror_y_center),
      ...PointAccumulate([
        { x: -s, y: -(2 * d + w + s) },
        { x: 0, y: -2 * s },
        { x: -1.5 * s, y: -1.5 * s },
        { x: 0, y: 3.5 * s - w }, // -(w + s - 4.5 * s)
        { x: (flaps ? 1.5 : 3) * s, y: -3 * s },
        ...flap_points,
      ] as BoxPointList),
    ]

    const cut_layer: BoxPointList[] = [
      [...cut_half, ...MirrorX(cut_half, mirror_x_center)].flat(),
      ...hole,
      ...MirrorX(hole, mirror_x_center),
    ]

    const fold_base = PointAccumulate(
      [
        { x: -s, y: -s },
        { x: 0, y: s - d },
      ] as BoxPointList,
      [
        { x: -2 * s, y: s / 2 - d },
        { x: 0, y: -(w / 3 + 1.5 * s) },
      ] as BoxPointList,
      [
        { x: -2 * s, y: -(d + s + (2 * w) / 3) },
        { x: 0, y: -(w / 3 + 1.5 * s) },
      ] as BoxPointList,
      [
        { x: -2 * s - d, y: s / 2 - d },
        { x: 0, y: -(w + 3 * s) },
      ] as BoxPointList,
      [
        { x: -4 * s - d, y: -(d + 0.5 * s) },
        { x: 0, y: -(w + s) },
      ] as BoxPointList,
      [
        { x: -s, y: -(2 * s + w + d) },
        { x: 0, y: s - d },
      ] as BoxPointList
    )

    const fold_layer = [
      ...fold_base,
      ...MirrorX(fold_base, mirror_x_center),
      ...PointAccumulate(
        [
          { x: -s, y: -d },
          { x: h + 3 * s, y: 0 },
        ] as BoxPointList,
        [
          { x: -s, y: -(d + w + 2 * s) },
          { x: h + 3 * s, y: 0 },
        ] as BoxPointList,
        [
          { x: -s, y: -(2 * d + w + 3 * s) },
          { x: h + 3 * s, y: 0 },
        ] as BoxPointList,
        [
          { x: (flaps ? -1 : 0.5) * s, y: -2 * (d + w + 2 * s) },
          { x: (flaps ? 3 : 0) * s + h, y: 0 },
        ] as BoxPointList,
        (flaps
          ? [
              { x: -s, y: -2 * (d + w + 2 * s) },
              { x: 0, y: -d },
            ]
          : []) as BoxPointList,
        (flaps
          ? [
              { x: 2 * s + h, y: -2 * (d + w + 2 * s) },
              { x: 0, y: -d },
            ]
          : []) as BoxPointList
      ),
    ]

    const offset_x = -(5 * s + 2 * d + FLAP_EXTRA) as Distance
    const offset_y = -(3 * d + 2 * w + 4 * s) as Distance

    return {
      [CUT_LAYER_ID]: [
        ...PlotPointsList(cut_layer, "cut", {
          offset_x,
          offset_y,
          closed: true,
        }),
      ],
      [FOLD_LAYER_ID]: [...PlotPointsList(fold_layer, "fold", { offset_x, offset_y })],
    } as PlotContribute
  }

export const PlotBoxAuto: GrowPlotter = PlotBoxAutoImpl(false)
export const PlotBoxAutoWithFlaps: GrowPlotter = PlotBoxAutoImpl(true)

export const BoxAutoOpeningGrow = (
  width: Distance,
  _height: Distance,
  depth: Distance = ZERO_DISTANCE,
  sheet_depth: Distance = ZERO_DISTANCE
): Distance =>
  (4 * depth + 11 * Max(sheet_depth, MIN_SHEET_SIZE) + width + 2 * FLAP_EXTRA) as Distance

export const BoxAutoReverseGrow = (
  height: Distance,
  _width: Distance,
  depth: Distance = ZERO_DISTANCE,
  sheet_depth: Distance = ZERO_DISTANCE
): Distance => (3 * depth + 4 * Max(sheet_depth, MIN_SHEET_SIZE) + 2 * height) as Distance
